import {
  matchesClaFlags,
  plb,
  text,
  year
} from '../../columns/columnSpec.types';
import type { CorrectionRowSpec } from './correctionSpec.types';

const c = (key: string) => text(`corrections.${key}`);
const cPlb = (key: string) => plb(`corrections.${key}`);
const cYear = (
  offset: number,
  fallbackKey: string,
  options?: { andOlder?: true; fallbackIsPlb?: true }
) => year(offset, `corrections.${fallbackKey}`, options);

/**
 * Ported from `EmployeeHoursCorrectionForm.js`. Row order is form order.
 *
 * Each `pair` is one Dojo `compound` row: the left column shows the current value, the right takes
 * the correction. Where Dojo used a `_` placeholder subfield the corresponding key is simply absent.
 */
export const HOURS_CORRECTION_ROWS: CorrectionRowSpec[] = [
  // Hours — :34-132
  { kind: 'section', label: c('hoursSection') },
  { kind: 'caption', label: c('corrections') },
  {
    kind: 'pair',
    label: c('cumulativeBeforeCorrection'),
    currentKey: 'hoursBalanceBase',
    signFlipWhenPlusMinus: true
  },
  {
    kind: 'pair',
    label: c('correctionHours'),
    editKey: 'inputWorkingHoursCor',
    signFlipWhenPlusMinus: true
  },
  {
    kind: 'pair',
    label: c('totalHoursStartValue'),
    currentKey: 'allHoursWorkTotal',
    signFlipWhenPlusMinus: true
  },
  {
    kind: 'pair',
    label: c('inputYhs'),
    editKey: 'inputJus',
    signFlipWhenPlusMinus: true
  },

  // Salary trigger — :135-160
  { kind: 'section', label: c('salaryTriggerSection') },
  {
    kind: 'flag',
    label: c('forceSalaryExport'),
    flagName: 'FORCE_SALARY_EXPORT'
  },

  // Memo — :162-176
  { kind: 'section', label: c('memoSection') },
  { kind: 'memo', label: c('memoSection'), editKey: 'inputWorkingMemo' },

  // Leave, statutory minimum — :178-242
  { kind: 'section', label: c('leaveSection') },
  { kind: 'caption', label: c('legalMinimum') },
  { kind: 'caption', label: c('corrections') },
  {
    kind: 'pair',
    label: cYear(0, 'correctionLeaveHours'),
    currentKey: 'hoursLeaveMin',
    editKey: 'inputHoursLeaveMin'
  },
  {
    kind: 'pair',
    label: cYear(1, 'previousYear'),
    currentKey: 'leaveMinPrev',
    editKey: 'inputHoursLeaveMinPrev'
  },

  // Leave above the statutory minimum — :244-418
  { kind: 'caption', label: c('overLegalMinimum') },
  {
    kind: 'pair',
    label: cYear(0, 'correctionLeaveHours'),
    currentKey: 'leaveOver',
    editKey: 'inputHoursLeaveOver'
  },
  {
    kind: 'pair',
    label: c('oneTimeAllowance'),
    currentKey: 'leaveOneTimeDepositRato',
    editKey: 'leaveOneTimeDeposit'
  },
  {
    kind: 'pair',
    label: cYear(1, 'previousYear'),
    currentKey: 'leaveOverPrev',
    editKey: 'inputHoursLeaveOverPrev'
  },
  {
    kind: 'pair',
    label: cYear(2, 'onePlusYear'),
    currentKey: 'overLeaveAge1',
    editKey: 'inputHoursLeave1Cor'
  },
  {
    kind: 'pair',
    label: cYear(3, 'twoPlusYears'),
    currentKey: 'overLeaveAge2',
    editKey: 'inputHoursLeave2Cor'
  },
  {
    kind: 'pair',
    label: cYear(4, 'threePlusYears'),
    currentKey: 'overLeaveAge3',
    editKey: 'inputHoursLeave3Cor'
  },
  {
    kind: 'pair',
    label: cYear(5, 'fourPlusYears'),
    currentKey: 'overLeaveAge4',
    editKey: 'inputHoursLeave4Cor'
  },
  {
    kind: 'pair',
    label: cYear(6, 'fivePlusYears', { andOlder: true }),
    currentKey: 'overLeaveAge5',
    editKey: 'inputHoursLeave5Cor'
  },

  // Compensation — :420-446
  { kind: 'caption', label: c('compensation') },
  {
    kind: 'pair',
    label: c('ageHoursCompensation'),
    currentKey: 'hoursCompLeaveAll',
    editKey: 'inputHoursAgeCor'
  },

  // Leave total — :449-470
  { kind: 'caption', label: c('leaveTotal') },
  {
    kind: 'pair',
    label: c('cumulativeBalance'),
    currentKey: 'hoursHolidayBalanceCum'
  },

  // PLB — :476-800, all gated on PLB_VISIBILITY
  {
    kind: 'section',
    label: cPlb('plbSection'),
    requiresFlag: 'PLB_VISIBILITY'
  },
  {
    kind: 'caption',
    label: cPlb('corrections'),
    requiresFlag: 'PLB_VISIBILITY'
  },
  {
    kind: 'pair',
    label: cYear(0, 'current', { fallbackIsPlb: true }),
    currentKey: 'plbCurr',
    editKey: 'inputHoursPlbCor',
    requiresFlag: 'PLB_VISIBILITY'
  },
  {
    kind: 'pair',
    label: cPlb('oneTimeAllowance'),
    currentKey: 'plbOneTimeDepositRato',
    editKey: 'plbOneTimeDeposit',
    requiresFlag: 'PLB_VISIBILITY'
  },
  {
    kind: 'pair',
    label: cYear(1, 'previousYear', { fallbackIsPlb: true }),
    currentKey: 'plbAgePrev',
    editKey: 'inputHoursPlbPrevCor',
    requiresFlag: 'PLB_VISIBILITY'
  },
  {
    kind: 'pair',
    label: cYear(2, 'onePlusYear', { fallbackIsPlb: true }),
    currentKey: 'plbAge1',
    editKey: 'inputHoursPlb1Cor',
    requiresFlag: 'PLB_VISIBILITY'
  },
  {
    kind: 'pair',
    label: cYear(3, 'twoPlusYears', { fallbackIsPlb: true }),
    currentKey: 'plbAge2',
    editKey: 'inputHoursPlb2Cor',
    requiresFlag: 'PLB_VISIBILITY'
  },
  {
    kind: 'pair',
    label: cYear(4, 'threePlusYears', { fallbackIsPlb: true }),
    currentKey: 'plbAge3',
    editKey: 'inputHoursPlb3Cor',
    requiresFlag: 'PLB_VISIBILITY'
  },
  {
    kind: 'pair',
    label: cYear(5, 'fourPlusYears', { fallbackIsPlb: true }),
    currentKey: 'plbAge4',
    editKey: 'inputHoursPlb4Cor',
    requiresFlag: 'PLB_VISIBILITY'
  },

  // Without PLB_10_YEARS plbAge5 is the "and older" bucket (:773-793); with it, each year gets its
  // own row and plbAge10 becomes the bucket (:643-771).
  {
    kind: 'pair',
    label: cYear(6, 'fivePlusYears', { andOlder: true, fallbackIsPlb: true }),
    currentKey: 'plbAge5',
    editKey: 'inputHoursPlb5Cor',
    requiresFlag: 'PLB_VISIBILITY',
    excludedByFlag: 'PLB_10_YEARS'
  },
  {
    kind: 'pair',
    label: cYear(6, 'fivePlusYears', { fallbackIsPlb: true }),
    currentKey: 'plbAge5',
    editKey: 'inputHoursPlb5Cor',
    requiresFlag: ['PLB_VISIBILITY', 'PLB_10_YEARS']
  },
  {
    kind: 'pair',
    label: cYear(7, 'sixPlusYears', { fallbackIsPlb: true }),
    currentKey: 'plbAge6',
    editKey: 'inputHoursPlb6Cor',
    requiresFlag: ['PLB_VISIBILITY', 'PLB_10_YEARS']
  },
  {
    kind: 'pair',
    label: cYear(8, 'sevenPlusYears', { fallbackIsPlb: true }),
    currentKey: 'plbAge7',
    editKey: 'inputHoursPlb7Cor',
    requiresFlag: ['PLB_VISIBILITY', 'PLB_10_YEARS']
  },
  {
    kind: 'pair',
    label: cYear(9, 'eightPlusYears', { fallbackIsPlb: true }),
    currentKey: 'plbAge8',
    editKey: 'inputHoursPlb8Cor',
    requiresFlag: ['PLB_VISIBILITY', 'PLB_10_YEARS']
  },
  {
    kind: 'pair',
    label: cYear(10, 'ninePlusYears', { fallbackIsPlb: true }),
    currentKey: 'plbAge9',
    editKey: 'inputHoursPlb9Cor',
    requiresFlag: ['PLB_VISIBILITY', 'PLB_10_YEARS']
  },
  {
    kind: 'pair',
    label: cYear(11, 'tenPlusYears', { andOlder: true, fallbackIsPlb: true }),
    currentKey: 'plbAge10',
    editKey: 'inputHoursPlb10Cor',
    requiresFlag: ['PLB_VISIBILITY', 'PLB_10_YEARS']
  },

  // PLB hours usage — :805-868. Note these are editable with no read-only counterpart.
  {
    kind: 'section',
    label: cPlb('plbHoursUsageSection'),
    requiresFlag: 'PLB_VISIBILITY'
  },
  {
    kind: 'pair',
    label: cPlb('plbHoursUsageWork'),
    editKey: 'hoursPlbUsedWork',
    requiresFlag: 'PLB_VISIBILITY'
  },
  {
    kind: 'pair',
    label: cPlb('plbHoursUsageLeave'),
    editKey: 'hoursPlbUsedLv',
    requiresFlag: 'PLB_VISIBILITY'
  },
  {
    kind: 'pair',
    label: cPlb('plbHoursUsageLcs'),
    editKey: 'hoursPlbUsedLcs',
    requiresFlag: 'PLB_VISIBILITY'
  },

  // PLB total — :870-891
  { kind: 'caption', label: cPlb('plbTotal'), requiresFlag: 'PLB_VISIBILITY' },
  {
    kind: 'pair',
    label: cPlb('cumulativeBalance'),
    currentKey: 'hoursPlbBalanceCum',
    requiresFlag: 'PLB_VISIBILITY'
  },

  // Life-cycle hours usage — :899-976. Not gated: Dojo showed this section unconditionally.
  { kind: 'section', label: c('lcsHoursUsageSection') },
  { kind: 'pair', label: c('lcsCorrectionInput'), editKey: 'inputHoursLcs' },
  { kind: 'pair', label: c('lcsHoursPension'), editKey: 'hoursLcsUsedFin' },
  { kind: 'pair', label: c('lcsHoursLifecycle'), editKey: 'hoursLcsUsedLife' },
  {
    kind: 'pair',
    label: c('cumulativeBalance'),
    currentKey: 'hoursLcsBalanceCum'
  },

  // Balance budget — :981-1022. Only for the last period of the year, and only with the grant.
  {
    kind: 'section',
    label: cPlb('balanceBudget'),
    requiresBalanceBudget: true
  },
  {
    kind: 'pair',
    label: cPlb('hourBalanceCorrection'),
    currentKey: 'savedPlusHours',
    requiresBalanceBudget: true
  },
  {
    kind: 'pair',
    label: cPlb('depositedLeave'),
    currentKey: 'savedExtraStatutoryLeaveHours',
    requiresBalanceBudget: true
  },
  {
    kind: 'pair',
    label: cPlb('depositedPlb'),
    currentKey: 'savedPLBHours',
    requiresBalanceBudget: true
  },
  {
    kind: 'note',
    label: cPlb('balanceBudgetDescription'),
    requiresBalanceBudget: true
  }
];

/**
 * Column source → the field holding its correction, so the detail window can put an input next to
 * the value it corrects without repeating the pairing.
 *
 * Two shapes count: the form's side-by-side pairs (`currentKey` is the column), and the corrections
 * that are a column themselves — `inputJus`, the budget and life-cycle usage inputs — which the form
 * showed with no read-only counterpart.
 */
export const correctionKeyForSource = (source: string) =>
  HOURS_CORRECTION_ROWS.flatMap((row) =>
    row.kind === 'pair' &&
    row.editKey &&
    (row.currentKey === source || (!row.currentKey && row.editKey === source))
      ? [row.editKey]
      : []
  )[0];

type VisibilityContext = {
  flags?: string[];
  /** period 12 plus the balance-budget view or edit grant */
  showBalanceBudget?: boolean;
};

export const visibleCorrectionRows = ({
  flags = [],
  showBalanceBudget = false
}: VisibilityContext): CorrectionRowSpec[] =>
  HOURS_CORRECTION_ROWS.filter((row) => {
    const requiresBalanceBudget =
      'requiresBalanceBudget' in row ? row.requiresBalanceBudget : undefined;

    return (
      matchesClaFlags(row, flags) &&
      (!requiresBalanceBudget || showBalanceBudget)
    );
  });

/** Every editable key the given context exposes, in form order. */
export const editableCorrectionKeys = (context: VisibilityContext) =>
  visibleCorrectionRows(context).flatMap((row) =>
    'editKey' in row && row.editKey ? [row.editKey] : []
  );
